import React, { Component } from 'react';
import { Button, Alert, Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import { SortableContainer, SortableElement, sortableHandle, arrayMove } from 'react-sortable-hoc';
import { apiClient } from '../../utils/Api';
import { REGISTRY, TEMPLATES, CATS, SETTING_NAME, fromWire, toWire, validateSection, cssUrl } from './registry';
import Inspector from './Inspector';
import Preview from './Preview';
import SourceModal from './SourceModal';
import TemplateThumb from './TemplateThumb';
import WebPreviewFrame from './WebPreviewFrame';
import './HomepageBuilder.css';

// When set, the web preview shows the REAL Next.js homepage in an iframe (live WYSIWYG)
// instead of the CMS-rendered approximation. e.g. http://localhost:3100/preview/homepage
const WEB_PREVIEW_URL = process.env.REACT_APP_WEB_PREVIEW_URL;

const DragHandle = sortableHandle(() => <i className="fa fa-bars handle" title="Drag to reorder" />);

const SortableRow = SortableElement(({ section: s, realIdx, selected, onSelect, onRemove, onMove, isFirst, isLast, platform }) => {
  const r = REGISTRY[s.type] || { label: s.type, icon: 'fa-cube' };
  const v = validateSection(s, platform);
  return (
    <div className={`hpb-sec ${selected === realIdx ? 'sel' : ''}`} role="button" tabIndex={0}
      aria-label={`Edit section: ${s.title || (REGISTRY[s.type] || {}).label || s.type}`}
      onClick={() => onSelect(realIdx)}
      onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(realIdx); } }}>
      <DragHandle />
      <div className="thumb" style={s.bannerImg ? { backgroundImage: `url('${cssUrl(s.bannerImg)}')` } : {}}>{s.bannerImg ? null : <i className={`fa ${r.icon}`} aria-hidden="true" />}</div>
      <div className="meta">
        <div className="t">
          {!v.valid ? <i className="fa fa-exclamation-circle text-danger mr-1" title={`Incomplete: ${v.reason}`} aria-label={`Incomplete: ${v.reason}`} /> : null}
          {s.title || r.label || '(untitled)'}
        </div>
        <div className="ty">{r.label}</div>
      </div>
      <div className="hpb-move">
        <button type="button" className="hpb-iconbtn" aria-label="Move section up" title="Move up" disabled={isFirst} onClick={(e) => { e.stopPropagation(); onMove(realIdx, -1); }}><i className="fa fa-chevron-up" aria-hidden="true" /></button>
        <button type="button" className="hpb-iconbtn" aria-label="Move section down" title="Move down" disabled={isLast} onClick={(e) => { e.stopPropagation(); onMove(realIdx, 1); }}><i className="fa fa-chevron-down" aria-hidden="true" /></button>
      </div>
      <div className="acts">
        <button type="button" className="hpb-iconbtn" aria-label="Remove section from page" title="Remove from page" onClick={(e) => { e.stopPropagation(); onRemove(realIdx); }}><i className="fa fa-trash" aria-hidden="true" /></button>
      </div>
    </div>
  );
});

const SortableList = SortableContainer(({ items, selected, onSelect, onRemove, onMove, platform }) => (
  <div>
    {items.map(({ section, realIdx }, listIdx) => (
      <SortableRow key={section._id} index={listIdx} section={section} realIdx={realIdx} selected={selected}
        onSelect={onSelect} onRemove={onRemove} onMove={onMove} platform={platform}
        isFirst={listIdx === 0} isLast={listIdx === items.length - 1} />
    ))}
  </div>
));

export default class HomepageBuilder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true, error: null, saving: false, savedMsg: null, savedOk: false,
      settingId: null, sections: [], selected: null, dirty: false,
      appTab: 'allCategory', device: 'desktop', addOpen: false, source: null,
      appCats: null, // category tabs from the app_subnav setting; null → fall back to CATS
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.load();
  }

  // Persist the activeTab fallback, so a tab that vanished can't silently pull the view back
  // when it reappears (assigning its category to another section rebuilds it). The guard means
  // at most one extra render, on the frame the tab drops.
  componentDidUpdate(prevProps, prevState) {
    // Only the three inputs of `cats`/`activeTab` can invalidate the tab, so unrelated updates
    // (device toggle, add-modal, save banner) skip the getter entirely.
    if (prevState.sections === this.state.sections
      && prevState.appCats === this.state.appCats
      && prevState.appTab === this.state.appTab) return;
    const tab = this.activeTab;
    if (this.state.appTab !== tab) this.setState({ appTab: tab });
  }

  componentWillUnmount() { this._mounted = false; }

  safeSet(s) { if (this._mounted) this.setState(s); }

  get name() { return SETTING_NAME[this.props.platform] || SETTING_NAME.web; }

  // Resolved category tabs: the app_subnav-driven list when available, else the hardcoded CATS.
  // Union in the default tab plus every homepage_category the stored layout actually uses:
  // visIndices() filters strictly by appTab, so a category with no tab would be invisible AND
  // uneditable here while save() still publishes it. Returns `base` untouched in the normal
  // case (nothing missing) so the reference stays stable across renders.
  get cats() {
    const base = this.state.appCats || CATS;
    if (this.props.platform !== 'app') return base;
    let out = base;
    if (!base.some((c) => c[0] === 'allCategory')) out = [['allCategory', 'All']].concat(base);
    this.state.sections.forEach((s) => {
      const v = s.homepage_category || 'allCategory';
      if (!out.some((c) => c[0] === v)) out = out.concat([[v, v]]);
    });
    return out;
  }

  // The tab list is dynamic (see cats), so appTab can point at a tab that just disappeared —
  // re-categorizing or removing the last section of a unioned orphan tab drops it. That would
  // render an empty list with no tab highlighted, so fall back to the default tab.
  get activeTab() {
    const t = this.state.appTab;
    return this.cats.some((c) => c[0] === t) ? t : 'allCategory';
  }

  loadAppCats() {
    // In-flight guard: Discard is re-enabled when the LAYOUT request settles, so without this
    // a second click could open a duplicate app_subnav request whose response order isn't
    // guaranteed. One request at a time makes a stale-response check unnecessary.
    // Every path that leaves the tabs on CATS says why: a silent fallback is indistinguishable
    // from a healthy setting, and it restores exactly the label drift this setting removes.
    const fallback = (reason) => {
      // eslint-disable-next-line no-console
      console.warn(`Homepage Builder: using default category tabs — ${reason}.`);
    };
    this._catsFetching = true;
    apiClient().get('/admins/settings?name=app_subnav')
      .then((res) => {
        this._catsFetching = false;
        const rows = (((res.data || {}).data || {}).data) || [];
        let value = (rows[0] || {}).value;
        if (typeof value === 'string') value = JSON.parse(value);
        if (!Array.isArray(value)) { fallback('the app_subnav setting is not a list'); return; }
        // app_subnav is hand-authored, so dedupe by homepage_category (first by sequence wins):
        // the value is a React key for the tab buttons and the inspector options, and a repeat
        // would also light two tabs at once (the active-tab test matches both).
        const seen = Object.create(null); // prototype-free: a category named `constructor`/`toString`/… must not read as already-seen
        const cats = value
          .slice()
          // Drop malformed rows BEFORE the sort and map dereference them: one null/undefined
          // entry in the hand-authored array throws, and the catch below would fall the whole
          // list back to the stale CATS labels this setting exists to replace.
          .filter((c) => c && c.homepage_category && c.title)
          .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
          .map((c) => [c.homepage_category, c.title])
          .filter(([val]) => !seen[val] && (seen[val] = true));
        if (cats.length) this.safeSet({ appCats: cats });
        else fallback('no app_subnav row has both a homepage_category and a title');
      })
      .catch((err) => {
        this._catsFetching = false; // failed (or threw in the handler): let the next load() retry
        fallback(`could not load app_subnav (${(err && err.message) || 'request failed'})`);
      });
  }

  load() {
    // The app category tabs are authored in the `app_subnav` setting (title + homepage_category
    // + sequence). Load them so the builder mirrors what's configured instead of a static list;
    // failure is non-fatal and falls back to CATS. Retried from here while still unresolved, so
    // Discard recovers a transient failure instead of pinning the stale labels for the session.
    // Web has no category tabs, so skip it there.
    if (this.props.platform === 'app' && !this.state.appCats && !this._catsFetching) this.loadAppCats();
    this.setState({ loading: true, error: null, selected: null, dirty: false, appTab: 'allCategory' });
    apiClient().get(`/admins/settings?name=${this.name}`)
      .then((res) => {
        const rows = (((res.data || {}).data || {}).data) || [];
        const row = rows[0];
        if (!row) { this.safeSet({ loading: false, error: `Setting "${this.name}" not found.` }); return; }
        let value = row.value;
        if (typeof value === 'string') {
          try { value = JSON.parse(value); }
          catch (e) {
            // Don't silently treat unparseable JSON as an empty layout — saving would
            // overwrite the live homepage with nothing. Block editing instead.
            this.safeSet({ loading: false, settingId: row.id, error: `The "${this.name}" setting is not valid JSON, so it can't be edited safely here. Fix it in the raw Setting editor first.` });
            return;
          }
        }
        if (!Array.isArray(value)) {
          this.safeSet({ loading: false, settingId: row.id, error: `The "${this.name}" setting is not a layout array.` });
          return;
        }
        const sections = value.map(fromWire);
        this.safeSet({ loading: false, settingId: row.id, sections });
      })
      .catch((err) => {
        // apiClient() only handles 503 globally — surface an expired session specifically so a
        // stale token on load is actionable, matching save()'s 401 branch.
        const status = err && err.response && err.response.status;
        const error = status === 401
          ? 'Session expired (401). Log in again, then reopen this page.'
          : (err && err.message) || 'Failed to load homepage layout.';
        this.safeSet({ loading: false, error });
      });
  }

  save() {
    if (this.state.settingId == null || this.state.error) return; // never publish over a failed/parse-error load
    const live = this.state.sections;
    // Gate: don't publish incomplete sections (they'd render empty on web).
    const invalid = live.map((s, i) => ({ s, i, v: validateSection(s, this.props.platform) })).filter((x) => !x.v.valid);
    if (invalid.length) {
      const first = invalid[0];
      const label = (REGISTRY[first.s.type] || {}).label || first.s.type;
      this.setState({ savedOk: false, savedMsg: `Can't publish: “${first.s.title || label}” ${first.v.reason}${invalid.length > 1 ? ` (+${invalid.length - 1} more incomplete)` : ''}.` });
      return;
    }
    // Publishing replaces the live homepage immediately for all users — confirm first.
    if (!window.confirm('Publish this layout to the LIVE homepage now? It replaces the current homepage immediately for all users.')) return;
    const wire = live.map((sec) => toWire(sec, this.props.platform));
    // Snapshot what we publish: if the user edits during the in-flight PUT, `sections`
    // becomes a new ref, so on success we only clear `dirty` when nothing changed since.
    const snapshot = live;
    this.setState({ saving: true, savedMsg: null });
    apiClient().put(`/admins/settings/${this.state.settingId}`, {
      name: this.name, value_type: 'json', value: JSON.stringify(wire, null, 2), active: true,
    })
      .then(() => this.safeSet({ saving: false, dirty: this.state.sections !== snapshot, savedMsg: 'Published to live.', savedOk: true }))
      .catch((err) => {
        // apiClient() only handles 503 globally — surface auth/validation failures specifically
        // so a publish failure is actionable instead of an opaque "Save failed".
        const res = err && err.response;
        const status = res && res.status;
        let savedMsg;
        if (status === 401) {
          savedMsg = 'Session expired (401). Log in again (a separate tab keeps this layout), then click Save to retry.';
        } else if (status === 422) {
          const errs = res.data && res.data.errors;
          const detail = (errs && (Object.values(errs)[0] || [])[0]) || (res.data && res.data.message);
          savedMsg = `Publish rejected (422): ${detail || 'the layout failed validation.'}`;
        } else {
          savedMsg = `Save failed${status ? ` (${status})` : ''}: ${(err && err.message) || 'error'}`;
        }
        this.safeSet({ saving: false, savedMsg, savedOk: false });
      });
  }

  // --- helpers ---
  visIndices() {
    const { sections } = this.state;
    const all = sections.map((_, i) => i);
    if (this.props.platform !== 'app') return all;
    const tab = this.activeTab;
    return all.filter((i) => (sections[i].homepage_category || 'allCategory') === tab);
  }

  patch(patch) {
    const i = this.state.selected;
    if (i == null) return;
    this.applyPatch(i, patch);
  }

  applyPatch(i, patch) {
    const sections = this.state.sections.slice();
    sections[i] = Object.assign({}, sections[i], patch);
    this.setState({ sections, dirty: true });
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const slots = this.visIndices();
    const sections = this.state.sections.slice();
    const items = slots.map((ix) => sections[ix]);
    const moved = arrayMove(items, oldIndex, newIndex);
    slots.forEach((ix, k) => { sections[ix] = moved[k]; });
    this.setState({ sections, selected: slots[newIndex], dirty: true });
  };

  // Keyboard/button reorder: move the section at realIdx up (-1) or down (+1) within its visible list.
  move(realIdx, dir) {
    const slots = this.visIndices();
    const pos = slots.indexOf(realIdx);
    const target = pos + dir;
    if (pos < 0 || target < 0 || target >= slots.length) return;
    const sections = this.state.sections.slice();
    const items = slots.map((ix) => sections[ix]);
    const moved = arrayMove(items, pos, target);
    slots.forEach((ix, k) => { sections[ix] = moved[k]; });
    this.setState({ sections, selected: slots[target], dirty: true });
  }

  remove(i) {
    const sections = this.state.sections.slice();
    sections.splice(i, 1);
    this.setState({ sections, selected: null, dirty: true });
  }

  addSection(type) {
    const r = REGISTRY[type];
    const base = fromWire({ type, homepage_category: this.props.platform === 'app' ? this.activeTab : 'allCategory' });
    // banner_only publishes no title (toWire strips it) — don't seed one, so a fresh
    // section looks the same as a loaded one.
    if (type !== 'banner_only') base.title = r.label;
    const sections = this.state.sections.concat([base]);
    this.setState({ sections, selected: sections.length - 1, dirty: true, addOpen: false });
  }

  applySource = ({ et, label, ep, slug }) => {
    const { idx, chipIdx } = this.state.source;
    if (chipIdx != null) {
      const sections = this.state.sections.slice();
      const sec = Object.assign({}, sections[idx]);
      const chips = (sec.chips || []).map((c) => Object.assign({}, c));
      chips[chipIdx] = Object.assign({}, chips[chipIdx], { endpoint_type: et, endpoint: ep, slug: slug || undefined });
      sec.chips = chips;
      sections[idx] = sec;
      this.setState({ sections, dirty: true, source: null });
      return;
    }
    const p = { endpoint_type: et, source: label, endpoint: ep, slug: slug || '' };
    this.applyPatch(idx, p);
    this.setState({ source: null });
  };

  renderAddModal() {
    return (
      <Modal isOpen toggle={() => this.setState({ addOpen: false })} size="lg">
        <ModalHeader toggle={() => this.setState({ addOpen: false })}>Add a section <small className="text-muted">— web &amp; app share the same layout types</small></ModalHeader>
        <ModalBody>
          <Row>
            {TEMPLATES.map(({ type, desc }) => {
              const r = REGISTRY[type];
              return (
                <Col xs="6" md="4" className="mb-3" key={type}>
                  <button type="button" className="hpb-tt-card" onClick={() => this.addSection(type)}>
                    <TemplateThumb type={type} />
                    <div className="hpb-tt-name">{r.label}</div>
                    <div className="hpb-tt-desc">{desc}</div>
                  </button>
                </Col>
              );
            })}
          </Row>
        </ModalBody>
      </Modal>
    );
  }

  render() {
    const { platform } = this.props;
    const { loading, error, sections, selected, dirty, saving, savedMsg, savedOk, device, source } = this.state;
    const appTab = this.activeTab; // normalized: never points at a tab the list no longer has
    const cats = this.cats; // once per render: the getter rebuilds (and on the union path reallocates)
    const vis = this.visIndices();
    const items = vis.map((realIdx) => ({ section: sections[realIdx], realIdx }));
    const platLabel = platform === 'app' ? 'App Homepage' : 'Web Homepage';

    return (
      <div className="hpb container-fluid" style={{ padding: 14 }}>
        <div className="hpb-bar">
          <span className="title"><i className={`fa ${platform === 'app' ? 'fa-mobile' : 'fa-desktop'}`} /> {platLabel}</span>
          <span className={`hpb-unsaved ${dirty ? 'show' : ''}`}><i className="fa fa-circle" style={{ fontSize: '.5rem' }} /> Unsaved changes</span>
          <span className="hpb-actions">
            <Button size="sm" color="secondary" outline disabled={loading || saving} onClick={() => this.load()}><i className="fa fa-undo" /> Discard</Button>
            <Button size="sm" color="success" disabled={loading || saving || !dirty} onClick={() => this.save()}>
              <i className="fa fa-cloud-upload" /> {saving ? 'Saving…' : 'Save (go live)'}
            </Button>
          </span>
        </div>

        {savedMsg ? <Alert color={savedOk ? 'success' : 'danger'} toggle={() => this.setState({ savedMsg: null })}>{savedMsg}</Alert> : null}
        {error ? <Alert color="danger">{error}</Alert> : null}
        {loading ? <Alert color="info"><i className="fa fa-spinner fa-spin" /> Loading homepage layout…</Alert> : null}

        {!loading && !error ? (
          <div className="hpb-grid">
            {/* LEFT: sections */}
            <div className="hpb-pane hpb-left">
              <div className="hpb-pane-head"><i className="fa fa-list" /> Sections <span className="badge badge-secondary ml-1">{vis.length}</span>
                <Button size="sm" color="primary" outline className="ml-auto" style={{ fontSize: '.72rem', padding: '2px 8px' }} onClick={() => this.setState({ addOpen: true })}><i className="fa fa-plus" /> Add</Button>
              </div>
              {platform === 'app' ? (
                <div className="hpb-cattabs">
                  {cats.map((c) => <button type="button" key={c[0]} className={`hpb-cattab ${appTab === c[0] ? 'on' : ''}`} aria-pressed={appTab === c[0]} onClick={() => this.setState({ appTab: c[0], selected: null })}>{c[1]}</button>)}
                </div>
              ) : null}
              <div className="hpb-pane-body">
                <SortableList items={items} selected={selected} platform={platform} onSortEnd={this.onSortEnd} useDragHandle helperClass="hpb-helper-drag"
                  onSelect={(i) => this.setState({ selected: i })} onRemove={(i) => this.remove(i)} onMove={(i, d) => this.move(i, d)} />
              </div>
            </div>

            {/* CENTER: preview */}
            <div className="hpb-pane hpb-center">
              <div className="hpb-prevbar">
                <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#5c6873' }}>
                  <i className={`fa ${platform === 'app' ? 'fa-mobile' : 'fa-desktop'}`} /> {platform === 'app' ? 'App preview' : 'Web preview'}
                  {platform !== 'app' && WEB_PREVIEW_URL ? <span className="badge badge-success ml-2">Live</span> : null}
                </span>
                {platform !== 'app' ? (
                  <span className="hpb-seg ml-auto">
                    <button type="button" aria-pressed={device === 'desktop'} className={device === 'desktop' ? 'on' : ''} onClick={() => this.setState({ device: 'desktop' })}><i className="fa fa-desktop" /> Desktop</button>
                    <button type="button" aria-pressed={device === 'mobile'} className={device === 'mobile' ? 'on' : ''} onClick={() => this.setState({ device: 'mobile' })}><i className="fa fa-mobile" /> Mobile</button>
                  </span>
                ) : null}
              </div>
              <div className="hpb-stage">
                {/* Web renders the live Next.js site in an iframe (true WYSIWYG), with a card-
                    approximation fallback when REACT_APP_WEB_PREVIEW_URL is unset. The app preview
                    is a structural list (Preview's app branch) — the Flutter UI can't be rendered
                    here, so it shows section order + content, not a visual mock. */}
                {platform === 'web' && WEB_PREVIEW_URL
                  ? <div style={{ width: '100%', height: 'calc(100vh - 270px)', minHeight: 460, borderRadius: 6, overflow: 'hidden' }}><WebPreviewFrame url={WEB_PREVIEW_URL} sections={sections} device={device} category="allCategory" /></div>
                  : platform === 'web'
                    ? <div className={`hpb-device ${device}`}><Preview sections={sections} indices={vis} selected={selected} onSelect={(i) => this.setState({ selected: i })} device={device} platform={platform} /></div>
                    : <div style={{ width: '100%' }}><Preview sections={sections} indices={vis} selected={selected} onSelect={(i) => this.setState({ selected: i })} device={device} platform={platform} /></div>}
              </div>
            </div>

            {/* RIGHT: inspector */}
            <div className="hpb-pane hpb-right">
              <div className="hpb-pane-head"><i className="fa fa-sliders" /> {selected != null && sections[selected] ? (REGISTRY[sections[selected].type] || {}).label || 'Edit section' : 'Edit section'}</div>
              <div className="hpb-pane-body">
                <Inspector section={selected != null ? sections[selected] : null} platform={platform} cats={cats}
                  onChange={(p) => this.patch(p)}
                  onChangeType={(t) => this.applyPatch(selected, { type: t })}
                  onOpenSource={(kind, chipIdx) => this.setState({ source: { idx: selected, kind, chipIdx } })}
                  onRemove={() => this.remove(selected)} />
              </div>
            </div>
          </div>
        ) : null}

        {this.state.addOpen ? this.renderAddModal() : null}
        {source ? <SourceModal kind={source.kind} platform={platform} title={source.chipIdx != null ? 'Chip filter' : null} category={(sections[source.idx] || {}).homepage_category || 'allCategory'} endpoint={source.chipIdx != null ? (((sections[source.idx] || {}).chips || [])[source.chipIdx] || {}).endpoint : (sections[source.idx] || {}).endpoint} onApply={this.applySource} onClose={() => this.setState({ source: null })} /> : null}
      </div>
    );
  }
}
